import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import browserHistory from '../utils/browserHistory';
import * as selectedItemsActions from '../selectedItems/selectedItemsSlice';
import { buildQueryString, toIsoDate } from '../utils/stringUtils';
import { fromApiFormat, toApiFormat } from '../utils/scenarioUtils';
import { deduplicatePaths } from '../utils/pathUtils';

import process from 'process';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {
  Edge,
  Node,
  Point,
  Interconnect,
  TSP,
  Overlay,
  ScenarioGroup,
  ScenarioGroupScenario,
  ScenarioListItem,
  Contract,
  Zone,
  Notice,
  Market,
  PaginatedResponseDict,
  CapacityComponent,
  CapacityComponentCriterionLink,
  Flow,
  FlowSliceKey,
  PriceComponent,
  SegmentConstraint,
  PointConstraint,
  Scenario,
  PriceSource,
  NoticeComponent,
  Organization,
  RiseChain,
  ContractGroup,
  PaginatedResponse,
  Rate,
  Trade,
  PathReport,
  PathReportQueryResponse,
  PathSpec,
  PriceDefinitionOptionsResponse,
  ResolvePathSpecResponse,
  SegmentContractOptionsResponse,
  KatanaPath,
  KatanaPathDetailsResponse,
  KatanaPathDumpRow,
  KatanaPathDumpQueryParams,
  KatanaPathSummary,
  KatanaPathingSummaryDay,
  KatanaPathingSummaryQueryParams,
  KatanaSequenceTranslationPreviewRequest,
  KatanaTSP,
  KatanaPoint,
  KatanaTranslationPreviewRow,
  KatanaTranslateToWatchlistRequest,
  KatanaTranslateToWatchlistResponse,
  KatanaContractUsageResponse,
  UserSession,
  PartialRecord,
  Dashboard,
  DashboardWidget,
  Region,
  RegionZone,
} from '@/types';
import { AdvancedFiltersState } from '@/filters/advancedFiltersSlice';
import { labelToBuyOrSell } from '../utils/priceUtils';
import { IOCContract } from '@/types';
import {
  RateSchedule,
  RateScheduleCategory,
  RateScheduleService,
  RatesPatch,
} from '@/types/api/RateSchedule';
import { TSPPriority } from '@/types/api/TSPPriority';
import { NoticeRecord } from '@/types/api/NoticeRecord';
import {
  MaintenanceNoticeRecord,
  NoticeSourceType,
} from '@/types/api/NoticeRecord';
import { NoticeComponentRecords } from '@/types/api/NoticeComponent';
import { ExtractedNotice, NoticeStatus } from '@/types/api/ExtractedNotice';
import { GetPathFlowsRequest, PathFlows } from '@/types/api/CapacityComponent';
export type { Edge, Node, Point, Interconnect, TSP, Overlay };

const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_API_URL}`,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('token');
    if (token) {
      headers.set('authorization', `Token ${token}`);
    }
    return headers;
  },
});

type TagType =
  | 'Point'
  | 'Node'
  | 'Edge'
  | 'Overlay'
  | 'Session'
  | 'Interconnect'
  | 'Contract'
  | 'ContractGroup'
  | 'TSP'
  | 'CapacityComponent'
  | 'PriceComponent'
  | 'Scenario'
  | 'ScenarioGroup'
  | 'IOC'
  | 'IOCContracts'
  | 'Notice'
  | 'Zone'
  | 'Organization'
  | 'RateSchedule'
  | 'PathReport'
  | 'PathReportAssets'
  | 'Watchlist'
  | 'Dashboard'
  | 'Region';

type QueryArgs = string | FetchArgs;
type QueryResponse<T extends unknown> = QueryReturnValue<
  T,
  FetchBaseQueryError,
  FetchBaseQueryMeta
>;

type UpdatePasswordPayload = {
  old_password: string;
  new_password: string;
  confirm_password: string;
};

const baseQueryWithReauth: BaseQueryFn<QueryArgs> = async (
  args,
  api,
  extraOptions,
) => {
  const result = (await baseQuery(
    args,
    api,
    extraOptions,
  )) as QueryResponse<unknown>;
  if (result.error && result.error.status === 401) {
    // we don't currently have a way to refresh the token, so just log them out so they go back to the login screen
    // remove bad token and send to login
    localStorage.removeItem('token');
    browserHistory.replace('/login');
  }
  return result;
};

// // dealing with bugs from getting ids as strings and as ints.  Would be better to do in django because it has to be called with unwrap too
// export const toString = (object: Record<string, any>) => {
//   Object.keys(object).forEach((k) => {
//     if (typeof object[k] === 'object') {
//       return toString(object[k]);
//     }

//     object[k] = `${object[k]}`;
//   });

//   return object;
// };

export const edgeApi = createApi({
  reducerPath: 'edgeApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Point',
    'Node',
    'Edge',
    'Overlay',
    'Session',
    'Interconnect',
    'Contract',
    'ContractGroup',
    'TSP',
    'CapacityComponent',
    'PriceComponent',
    'PriceComponentFlow',
    'Scenario',
    'ScenarioGroup',
    'IOC',
    'IOCContracts',
    'Notice',
    'Zone',
    'Organization',
    'RateSchedule',
    'PathReport',
    'PathReportAssets',
    'Watchlist',
    'Dashboard',
    'Region',
  ] as const,
  endpoints: (build) => ({
    loginUser: build.mutation<void, { username: string; password: string }>({
      query: ({ username, password }) => ({
        url: '/api-token-auth/',
        method: 'POST',
        body: { username: username.toLocaleLowerCase(), password },
      }),
      invalidatesTags: [{ type: 'Session' }, { type: 'Organization' }],
    }),

    getPermissions: build.query<UserSession, void>({
      query: () => '/api/user-session/',
      providesTags: () => [{ type: 'Session' }],
    }),

    updatePassword: build.mutation<
      { message: string; first_time?: boolean },
      UpdatePasswordPayload
    >({
      query: (passwords) => ({
        url: '/api/user/update-password/',
        method: 'POST',
        body: passwords,
      }),
      invalidatesTags: [{ type: 'Session' }],
    }),

    getMapLayout: build.query({
      query: () => '/api/map/layout/',
    }),

    getMapPoints: build.query<Record<string, Point>, void>({
      query: () => '/api/map/points/',
      providesTags: [{ type: 'Point', id: 'LIST' }],
    }),

    createPoint: build.mutation({
      query: (point: Partial<Point>) => ({
        url: '/api/points/',
        method: 'POST',
        body: point,
      }),
      transformResponse: (response: QueryResponse<Point>) => response.data,
    }),

    updatePoint: build.mutation({
      query: ({ id, ...patch }: Point) => ({
        url: `/api/points/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      transformResponse: (response: QueryResponse<Point>) => response.data,
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled;
          const { data: pointResult } = response;
          if (pointResult) {
            dispatch(
              edgeApi.util.updateQueryData(
                'getMapPoints',
                undefined,
                (draft) => {
                  draft[pointResult?.id] = pointResult;
                  return draft;
                },
              ),
            );
          }
        } catch (error) {
          console.error('Error updating point:', error);
        }
      },
    }),

    createUpdatePoint: build.mutation({
      async queryFn(
        { point, node }: { point: Point; node: Node },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const nodeId = node.id;
        if (nodeId === 'new' || !nodeId) {
          const nodeRes = (await fetchWithBQ({
            url: '/api/nodes/',
            method: 'POST',
            body: node,
          })) as QueryResponse<Node>;
          point.node = Number(nodeRes.data?.id);
        }
        let pointRes;

        if (point.id === 'new' || !point.id) {
          pointRes = (await fetchWithBQ({
            url: '/api/points/',
            method: 'POST',
            body: point,
          })) as QueryResponse<Point>;
        } else {
          pointRes = (await fetchWithBQ({
            url: `/api/points/${point.id}/`,
            method: 'PATCH',
            body: point,
          })) as QueryResponse<Point>;
        }
        return pointRes.data
          ? { data: pointRes.data }
          : { error: pointRes.error };
      },
      invalidatesTags: [
        { type: 'Point', id: 'LIST' },
        { type: 'Node', id: 'LIST' },
        { type: 'Interconnect', id: 'LIST' },
      ],
      async onQueryStarted({ node }, { dispatch, queryFulfilled }) {
        try {
          let queryResponse;
          try {
            queryResponse = await queryFulfilled;
          } catch {
            console.error('error with createUpdatePoint query', queryResponse);
            return;
          }
          const { data: pointResult } = queryResponse;
          let nodeId = pointResult?.node;
          if (pointResult) {
            dispatch(
              edgeApi.util.updateQueryData(
                'getMapPoints',
                undefined,
                (draft) => {
                  draft[pointResult?.id ?? ''] = pointResult;
                  return draft;
                },
              ),
            );
          }
          dispatch(
            edgeApi.util.updateQueryData(
              'getMapNodes',
              undefined,
              (draft: any) => {
                const updatedNode = {
                  ...node,
                  id: nodeId,
                  points: [pointResult?.id],
                  isLocal: false,
                };
                draft[nodeId ?? ''] = updatedNode;
                return draft;
              },
            ),
          );
          dispatch(
            selectedItemsActions.itemClicked({
              itemType: 'node',
              id: nodeId,
              preventToggle: true,
            }),
          );
        } catch (e) {
          console.error(e);
        }
      },
    }),

    deletePoint: build.mutation<void, string | number>({
      query: (pointId) => ({
        url: `/api/points/${pointId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Point', id: 'LIST' }],
    }),

    getMapNodes: build.query({
      query: () => '/api/map/nodes/',
      providesTags: [{ type: 'Node', id: 'LIST' }],
      transformResponse: (response: Record<string, Node>) => response,
    }),

    createNode: build.mutation({
      query: (node: Partial<Node>) => ({
        url: '/api/nodes/',
        method: 'POST',
        body: node,
      }),
      async onQueryStarted(_node, { dispatch, queryFulfilled }) {
        try {
          const queryResult = (await queryFulfilled) as QueryResponse<Node>;
          const { data: nodeResult } = queryResult;
          dispatch(
            edgeApi.util.updateQueryData(
              'getMapNodes',
              undefined,
              (draft: any) => {
                draft[nodeResult?.id ?? ''] = nodeResult;
                return draft;
              },
            ),
          );
          nodeResult?.points.forEach((pointId) => {
            dispatch(
              edgeApi.util.updateQueryData(
                'getMapPoints',
                undefined,
                (draft) => {
                  draft[pointId].node = Number(nodeResult.id);
                  return draft;
                },
              ),
            );
          });
        } catch (error) {
          console.error('Error creating node:', error);
        }
      },
    }),

    updateNode: build.mutation<
      Node,
      { id: string | number; patch: Partial<Node> }
    >({
      query: ({ id, patch }) => ({
        url: `/api/nodes/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const queryResult = await queryFulfilled;
          const { data: nodeResult } = queryResult;
          if (!nodeResult) return;
          dispatch(
            edgeApi.util.updateQueryData('getMapNodes', undefined, (draft) => {
              draft[nodeResult.id] = nodeResult;
              return draft;
            }),
          );
          nodeResult.starts_edges.forEach((edgeId) => {
            dispatch(
              edgeApi.util.updateQueryData(
                'getMapEdges',
                undefined,
                (draft) => {
                  draft[edgeId].positions = [
                    [Number(nodeResult.lat), Number(nodeResult.lng)],
                    draft[edgeId].positions[1],
                  ];
                  return draft;
                },
              ),
            );
          });
          nodeResult.ends_edges.forEach((edgeId) => {
            dispatch(
              edgeApi.util.updateQueryData(
                'getMapEdges',
                undefined,
                (draft) => {
                  draft[edgeId].positions = [
                    draft[edgeId].positions[0],
                    [Number(nodeResult.lat), Number(nodeResult.lng)],
                  ];
                  return draft;
                },
              ),
            );
          });
        } catch (error) {
          console.error('Error updating node:', error);
        }
      },
    }),

    deleteNode: build.mutation<void, string | number>({
      query: (nodeId) => ({
        url: `api/nodes/${nodeId}/`,
        method: 'DELETE',
      }),
      async onQueryStarted(nodeId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          let cascadeEdges: number[] = [];
          let cascadePoints: number[] = [];
          dispatch(
            edgeApi.util.updateQueryData('getMapNodes', undefined, (draft) => {
              draft[nodeId]?.starts_edges.forEach((edgeId) =>
                cascadeEdges.push(edgeId),
              );
              draft[nodeId]?.ends_edges.forEach((edgeId) =>
                cascadeEdges.push(edgeId),
              );
              cascadePoints.push(...draft[nodeId].points);
              delete draft[nodeId];
              return draft;
            }),
          );
          if (cascadeEdges.length > 0) {
            dispatch(
              edgeApi.util.updateQueryData(
                'getMapEdges',
                undefined,
                (draft) => {
                  cascadeEdges.forEach((edgeId) => {
                    delete draft[edgeId];
                  });
                  return draft;
                },
              ),
            );
          }
          if (cascadePoints.length > 0) {
            dispatch(
              edgeApi.util.updateQueryData(
                'getMapPoints',
                undefined,
                (draft) => {
                  cascadePoints.forEach((pointId) => {
                    draft[pointId].node = null;
                  });
                  return draft;
                },
              ),
            );
          }
        } catch (error) {
          console.error('Error deleting node:', error);
        }
      },
    }),

    addNodePoint: build.mutation<void, { nodeId: number; pointId: number }>({
      query: ({ nodeId, pointId }) => ({
        url: `/api/nodes/${nodeId}/add_point/`,
        method: 'PUT',
        body: { point_id: pointId },
      }),
      async onQueryStarted({ nodeId, pointId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            edgeApi.util.updateQueryData('getMapNodes', undefined, (draft) => {
              draft[nodeId].points.push(pointId);
              return draft;
            }),
          );
          dispatch(
            edgeApi.util.updateQueryData('getMapPoints', undefined, (draft) => {
              draft[pointId].node = nodeId;
              return draft;
            }),
          );
        } catch (error) {
          console.error('Error adding point to node:', error);
        }
      },
    }),

    removeNodePoint: build.mutation({
      query: ({ nodeId, pointId }) => ({
        url: `/api/nodes/${nodeId}/remove_point/`,
        method: 'PATCH',
        body: { point_id: pointId },
      }),
      async onQueryStarted({ nodeId, pointId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            edgeApi.util.updateQueryData('getMapNodes', undefined, (draft) => {
              draft[nodeId].points = draft[nodeId].points.filter(
                (point) => point !== pointId,
              );
              return draft;
            }),
          );
          dispatch(
            edgeApi.util.updateQueryData('getMapPoints', undefined, (draft) => {
              draft[pointId].node = null;
              return draft;
            }),
          );
        } catch (error) {
          console.error('Error removing point from node:', error);
        }
      },
    }),

    getMapEdges: build.query<Record<string, Edge>, void>({
      query: () => '/api/map/edges/',
      providesTags: [{ type: 'Edge', id: 'LIST' }],
    }),

    createUpdateEdge: build.mutation<Edge, Partial<Edge>>({
      async queryFn(edge, _queryApi, _extraOptions, fetchWithBQ) {
        let edgeRes: QueryReturnValue<Edge> | undefined;
        if (edge.id === 'new' || !edge.id) {
          edgeRes = (await fetchWithBQ({
            url: '/api/edges/',
            method: 'POST',
            body: edge,
          })) as QueryReturnValue<Edge>;
        } else {
          edgeRes = (await fetchWithBQ({
            url: `/api/edges/${edge.id}/`,
            method: 'PATCH',
            body: edge,
          })) as QueryReturnValue<Edge>;
        }
        return edgeRes;
      },
      async onQueryStarted(_edge, { dispatch, queryFulfilled }) {
        try {
          const queryResult = await queryFulfilled;
          const { data: edgeResult } = queryResult;
          dispatch(
            edgeApi.util.updateQueryData('getMapEdges', undefined, (draft) => {
              draft[edgeResult.id] = edgeResult;
              return draft;
            }),
          );
        } catch (error) {
          console.error('Error creating edge:', error);
        }
      },
    }),

    deleteEdge: build.mutation<void, number | string>({
      query: (edgeId) => ({
        url: `/api/edges/${edgeId}/`,
        method: 'DELETE',
      }),
      async onQueryStarted(edgeId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            edgeApi.util.updateQueryData('getMapEdges', undefined, (draft) => {
              delete draft[edgeId];
              return draft;
            }),
          );
        } catch (error) {
          console.error('Error deleting edge:', error);
        }
      },
    }),

    splitEdge: build.mutation<
      { edges: Record<string, Edge>; nodes: Record<string, Node> },
      { node: Partial<Node>; edgeId: number }
    >({
      query: ({ node, edgeId }) => ({
        url: `/api/edges/${edgeId}/split/`,
        method: 'PUT',
        body: { node },
      }),
      async onQueryStarted(_params, { dispatch, queryFulfilled }) {
        try {
          const queryResult = await queryFulfilled;
          const { data: splitResult } = queryResult;
          if (splitResult) {
            dispatch(
              edgeApi.util.updateQueryData(
                'getMapEdges',
                undefined,
                (draft) => {
                  Object.assign(draft, splitResult.edges);
                  return draft;
                },
              ),
            );
            dispatch(
              edgeApi.util.updateQueryData(
                'getMapNodes',
                undefined,
                (draft) => {
                  Object.assign(draft, splitResult.nodes);
                  return draft;
                },
              ),
            );
            dispatch(
              edgeApi.util.updateQueryData(
                'getMapPoints',
                undefined,
                (draft) => {
                  Object.values(splitResult.nodes).forEach((node) => {
                    node.points.forEach((pointId) => {
                      if (draft[pointId]) {
                        draft[pointId].node = Number(node.id);
                      }
                    });
                  });
                  return draft;
                },
              ),
            );
          }
        } catch (error) {
          console.error('Error splitting edge:', error);
        }
      },
    }),

    getInterconnects: build.query({
      query: () => '/api/interconnects/',
      transformResponse: (response: {
        results: Record<string, Interconnect>;
      }) => response.results,
      providesTags: [{ type: 'Interconnect', id: 'LIST' }],
    }),

    createInterconnect: build.mutation<Interconnect, Partial<Interconnect>>({
      query: (interconnect) => ({
        url: '/api/interconnects/',
        method: 'POST',
        body: interconnect,
      }),
      async onQueryStarted(_interconnect, { dispatch, queryFulfilled }) {
        try {
          const { data: interconnectResult } = await queryFulfilled;
          dispatch(
            edgeApi.util.updateQueryData(
              'getInterconnects',
              undefined,
              (draft) => {
                draft[interconnectResult.id] = interconnectResult;
                return draft;
              },
            ),
          );
        } catch (error) {
          console.error('Error creating interconnect:', error);
        }
      },
    }),

    disableInterconnect: build.mutation<void, string | number>({
      query: (id) => ({
        url: `api/interconnects/${id}/disable/`,
        method: 'PUT',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            edgeApi.util.updateQueryData(
              'getInterconnects',
              undefined,
              (draft) => {
                draft[id].enabled = false;
                return draft;
              },
            ),
          );
        } catch (error) {
          console.error('Error disabling interconnect:', error);
        }
      },
    }),

    enableInterconnect: build.mutation<void, string | number>({
      query: (id) => ({
        url: `api/interconnects/${id}/enable/`,
        method: 'PUT',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            edgeApi.util.updateQueryData(
              'getInterconnects',
              undefined,
              (draft) => {
                draft[id].enabled = true;
                return draft;
              },
            ),
          );
        } catch (error) {
          console.error('Error enabling interconnect:', error);
        }
      },
    }),

    deleteInterconnect: build.mutation<void, string | number>({
      query: (id) => ({
        url: `/api/interconnects/${id}/`,
        method: 'DELETE',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            edgeApi.util.updateQueryData(
              'getInterconnects',
              undefined,
              (draft) => {
                delete draft[id];
                return draft;
              },
            ),
          );
        } catch (error) {
          console.error('Error deleting interconnect:', error);
        }
      },
    }),

    getPath: build.query<
      Record<string, Array<[number, number]>>,
      { nodes: number[] }
    >({
      query: ({ nodes }) => {
        return `/api/map/path/?nodes=${nodes.join(',')}`;
      },
    }),

    getOverlays: build.query<Overlay[], void>({
      query: () => '/api/overlays/',
      transformResponse: (response: { results: Overlay[] }) => response.results,
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [{ type: 'Overlay', id: 'LIST' }]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Posts', id: 'LIST' }` is invalidated
            [{ type: 'Overlay', id: 'LIST' }],
    }),

    createUpdateOverlay: build.mutation<Overlay, Partial<Overlay>>({
      async queryFn(overlay, _queryApi, _extraOptions, fetchWithBQ) {
        let overlayRes: QueryReturnValue<Overlay>;
        if (overlay.id === 'new' || !overlay.id) {
          overlayRes = (await fetchWithBQ({
            url: '/api/overlays/',
            method: 'POST',
            body: overlay,
          })) as QueryReturnValue<Overlay>;
        } else {
          overlayRes = (await fetchWithBQ({
            url: `/api/overlays/${overlay.id}/`,
            method: 'PATCH',
            body: overlay,
          })) as QueryReturnValue<Overlay>;
        }

        return overlayRes;
      },
      invalidatesTags: [{ type: 'Overlay', id: 'LIST' }],
    }),

    deleteOverlay: build.mutation({
      query: (overlayId) => ({
        url: `/api/overlays/${overlayId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Overlay', id: 'LIST' }],
    }),

    createScenarioGroupScenario: build.mutation<
      ScenarioGroupScenario,
      Partial<ScenarioGroupScenario>
    >({
      async queryFn(
        scenarioGroupScenario,
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const res = (await fetchWithBQ({
          url: '/api/scenario_group_scenarios/',
          method: 'POST',
          body: scenarioGroupScenario,
        })) as QueryReturnValue<ScenarioGroupScenario>;
        return res.data ? { data: res.data } : { error: res.error };
      },
    }),

    deleteScenarioGroupScenario: build.mutation({
      query: (id) => ({
        url: `/api/scenario_group_scenario/${id}/`,
        method: 'DELETE',
      }),
      // invalidatesTags: [{ type: 'Overlay', id: 'LIST'}],
    }),

    startScenarioAllPaths: build.mutation({
      query: ({ scenarioId, sortBy = 'chain_profit', filters = {} }) => {
        const queryParams = buildQueryString({ sort_by: sortBy, ...filters });
        return {
          url: `/api/scenarios/${scenarioId}/all_paths/?${queryParams}`,
          method: 'POST',
        };
      },
    }),

    getScenarioAllPaths: build.query({
      query: ({
        scenarioId,
        sortBy = 'chain_profit',
        filters = {},
        pageSize,
        offset,
      }) => {
        const queryParams = buildQueryString({
          sort_by: sortBy,
          page_size: pageSize,
          offset,
          ...filters,
        });
        return `/api/scenarios/${scenarioId}/all_paths/?${queryParams}`;
      },
    }),

    pickFromScenarioAllPaths: build.mutation({
      query: ({
        scenarioId,
        chains,
        sortBy = 'chain_profit',
        filters = {},
      }) => {
        const queryParams = buildQueryString({ sort_by: sortBy, ...filters });
        return {
          url: `/api/scenarios/${scenarioId}/all_paths/?${queryParams}`,
          method: 'PUT',
          body: {
            chains,
          },
        };
      },
    }),

    getScenario: build.query<
      Scenario,
      {
        scenarioId?: string | number;
        scenarioGroupId?: string | number;
      }
    >({
      query: ({ scenarioId, scenarioGroupId } = {}) => {
        const queryParams = buildQueryString({ scenarioGroupId });
        return `/api/scenarios/${scenarioId}/details/?${queryParams}`;
      },
      transformResponse: (response: Record<string, any>, _meta, _arg) =>
        fromApiFormat(response),
      providesTags: (result) =>
        result ? [{ type: 'Scenario' }] : [{ type: 'Scenario', id: 'LIST' }],
    }),

    getScenarios: build.query({
      query: ({
        all = false,
        search,
        system,
        flow_date,
        limit,
        offset,
      }: {
        all: boolean;
        search: string;
        system: boolean;
        flow_date: Date | string;
        limit: number;
        offset: number;
      }) => {
        const queryParams = buildQueryString({
          all,
          search,
          system,
          flow_date: flow_date ? toIsoDate(flow_date) : null,
          limit,
          offset,
        });
        return `/api/scenarios/?${queryParams}`;
      },
      transformResponse: (response: { results: ScenarioListItem[] }) =>
        response.results,
      providesTags: () => [{ type: 'Scenario', id: 'LIST' }],
    }),

    getScenarioGroups: build.query({
      query: () => '/api/scenario_groups/',
      transformResponse: (response: { results: ScenarioGroup[] }) =>
        response.results,
      providesTags: () => [{ type: 'ScenarioGroup', id: 'LIST' }],
    }),

    getScenarioGroup: build.query({
      query: (scenarioGroupId) => `/api/scenario_groups/${scenarioGroupId}/`,
      providesTags: (result) =>
        result
          ? [{ type: 'ScenarioGroup', id: result.id }]
          : [{ type: 'ScenarioGroup', id: 'LIST' }],
    }),

    createUpdateScenarioGroup: build.mutation({
      async queryFn(
        scenarioGroup: Partial<ScenarioGroup>,
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        let scenarioGroupRes: QueryReturnValue<ScenarioGroup>;
        if (scenarioGroup.id === 'new' || !scenarioGroup.id) {
          scenarioGroupRes = (await fetchWithBQ({
            url: '/api/scenario_groups/',
            method: 'POST',
            body: scenarioGroup,
          })) as QueryReturnValue<ScenarioGroup>;
        } else {
          scenarioGroupRes = (await fetchWithBQ({
            url: `/api/scenario_groups/${scenarioGroup.id}/`,
            method: 'PATCH',
            body: scenarioGroup,
          })) as QueryReturnValue<ScenarioGroup>;
        }
        return scenarioGroupRes.data
          ? { data: scenarioGroupRes.data }
          : { error: scenarioGroupRes.error };
      },
      invalidatesTags: [{ type: 'ScenarioGroup', id: 'LIST' }],
    }),

    getContracts: build.query({
      query: ({
        limit,
        offset,
        date,
        organizations,
        include_details,
        search = '',
        ids = [],
        tsp_ids = [],
        organization_ids = [],
        types = [],
        has_points,
        is_active_on_date,
        use_in_scenarios,
      }: {
        limit?: number;
        offset?: number;
        date?: string;
        organizations?: number[];
        include_details?: boolean;
        search?: string;
        ids?: number[];
        tsp_ids?: number[];
        organization_ids?: number[];
        types?: number[];
        has_points?: boolean;
        is_active_on_date?: boolean;
        use_in_scenarios?: boolean;
      } = {}) => {
        const queryParams = buildQueryString({
          limit,
          offset,
          date,
          include_details,
          organizations,
          search,
          id__in: ids.join(','),
          tsp__id__in: tsp_ids.join(','),
          organization__id__in: organization_ids.join(','),
          type__in: types.join(','),
          has_points,
          is_active_on_date,
          use_in_scenarios,
        });
        return `/api/contracts/?${queryParams}`;
      },
      transformResponse: (response: { results: Contract[] }) =>
        response.results,
      providesTags: [{ type: 'Contract', id: 'LIST' }],
    }),

    getContract: build.query<Contract, number | string>({
      query: (contractId) => `/api/contracts/${contractId}/`,
      providesTags: (result) =>
        result
          ? [{ type: 'Contract', id: result.id }]
          : [{ type: 'Contract', id: 'LIST' }],
    }),

    createContractGroup: build.mutation({
      query: ({ name, contracts }) => ({
        url: '/api/contract_groups/',
        method: 'POST',
        body: { name, contracts },
      }),
      invalidatesTags: [{ type: 'ContractGroup', id: 'LIST' }],
    }),

    getContractGroups: build.query({
      query: () => '/api/contract_groups/',
      transformResponse: (response: {
        results: Record<string, ContractGroup>;
      }) => response.results,
      providesTags: [{ type: 'ContractGroup', id: 'LIST' }],
    }),

    createRateSchedule: build.mutation<RateSchedule, Partial<RateSchedule>>({
      query: (rateSchedule) => ({
        url: '/api/rate_schedules/',
        method: 'POST',
        body: rateSchedule,
      }),
      invalidatesTags: [{ type: 'RateSchedule', id: 'LIST' }],
    }),

    getRates: build.query<
      RateSchedule[],
      {
        tsp?: number | string;
        category?: RateScheduleCategory;
        service?: RateScheduleService;
        search?: string;
      }
    >({
      query: ({ tsp, category, service, search }) => {
        const args = buildQueryString({
          tsp__id: tsp,
          category,
          service,
          search,
        });
        return `/api/rate_schedules/?${args}`;
      },
      providesTags: [{ type: 'RateSchedule', id: 'LIST' }],
      transformResponse: (response: PaginatedResponse<RateSchedule>) =>
        response.results,
    }),

    addRate: build.mutation<
      RateSchedule[],
      { rate: Partial<Rate>; rateScheduleId: number }
    >({
      query: ({ rate, rateScheduleId }) => ({
        url: `/api/rate_schedules/${rateScheduleId}/rates/`,
        method: 'POST',
        body: rate,
      }),
      invalidatesTags: [{ type: 'RateSchedule', id: 'LIST' }],
    }),

    updateRates: build.mutation<RateSchedule[], RatesPatch>({
      query: ({ rate_schedule_id, rates }) => ({
        url: `/api/rate_schedules/${rate_schedule_id}/rates/`,
        method: 'PATCH',
        body: rates,
      }),
      invalidatesTags: [{ type: 'RateSchedule', id: 'LIST' }],
    }),

    removeRate: build.mutation<
      void,
      { rateScheduleId: number; rateId: number }
    >({
      query: ({ rateScheduleId, rateId }) => ({
        url: `/api/rate_schedules/${rateScheduleId}/rates?rate_id=${rateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'RateSchedule', id: 'LIST' }],
    }),

    getTSPs: build.query<Record<string, TSP>, void>({
      query: () => '/api/tsps/',
      transformResponse: (response: { results: Record<string, TSP> }) =>
        response.results,
      providesTags: [{ type: 'TSP', id: 'LIST' }],
    }),

    getTSPPriorities: build.query<Record<string, TSPPriority>, void>({
      query: () => '/api/tsps/priorities/',
      providesTags: [{ type: 'TSP', id: 'PRIORITIES' }],
    }),

    getZones: build.query({
      query: ({ tsp }: { tsp?: number | string } = {}) => {
        const args = buildQueryString({ tsp });
        return `/api/zones/?${args}`;
      },
      transformResponse: (response: { results: Record<string, Zone> }) =>
        response.results,
      providesTags: [{ type: 'Zone', id: 'LIST' }],
    }),

    getRegions: build.query({
      query: () => '/api/regions/',
      transformResponse: (response: { results: Region[] }) => response.results,
      providesTags: [{ type: 'Region', id: 'LIST' }],
    }),

    addTSPToRegion: build.mutation<
      Region,
      { regionId: number; tsp: Partial<number> }
    >({
      query: ({ regionId, tsp }) => ({
        url: `/api/regions/${regionId}/add_tsp/`,
        method: 'POST',
        body: { tsp },
      }),
      onQueryStarted: async (
        { regionId, tsp: tspId },
        { dispatch, queryFulfilled, ...api },
      ) => {
        const state = api.getState();
        const tspSelector = edgeApi.endpoints.getTSPs.select();
        const tsp = tspSelector(state)?.data?.[tspId];
        const regionSelector = edgeApi.endpoints.getRegions.select(undefined);
        const region = regionSelector(state)?.data?.find(
          (r) => r.id === regionId,
        );
        const { undo: undoRegion } = dispatch(
          edgeApi.util.updateQueryData('getRegions', undefined, (draft) => {
            const index = draft.findIndex((region) => region.id === regionId);
            if (tsp && index !== -1) {
              draft[index].tsps.push(tsp);
            }
          }),
        );
        const { undo: undoTSP } = dispatch(
          edgeApi.util.updateQueryData('getTSPs', undefined, (draft) => {
            if (region) {
              draft[tspId]?.regions.push({
                id: region.id,
                name: region.name,
              });
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch (e) {
          console.error(e);
          undoRegion();
          undoTSP();
        }
      },
    }),

    removeTSPFromRegion: build.mutation<
      Region,
      { regionId: number; tsp: Partial<number> }
    >({
      query: ({ regionId, tsp }) => ({
        url: `/api/regions/${regionId}/remove_tsp/`,
        method: 'POST',
        body: { tsp },
      }),
      onQueryStarted: async (
        { regionId, tsp: tspId },
        { dispatch, queryFulfilled },
      ) => {
        const { undo: undoRegion } = dispatch(
          edgeApi.util.updateQueryData('getRegions', undefined, (draft) => {
            const index = draft.findIndex((region) => region.id === regionId);
            if (index !== -1) {
              draft[index].tsps = draft[index].tsps.filter(
                (tsp) => tsp.id !== tspId,
              );
            }
          }),
        );
        const { undo: undoTSP } = dispatch(
          edgeApi.util.updateQueryData('getTSPs', undefined, (draft) => {
            if (draft[tspId]) {
              draft[tspId].regions = draft[tspId].regions.filter(
                (region) => region.id !== regionId,
              );
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch (e) {
          console.error(e);
          undoRegion();
          undoTSP();
        }
      },
    }),

    addZoneToRegion: build.mutation<Region, { regionId: number; zone: number }>(
      {
        query: ({ regionId, zone }) => ({
          url: `/api/regions/${regionId}/add_zone/`,
          method: 'POST',
          body: { zone },
        }),
        onQueryStarted: async (
          { regionId, zone: zoneId },
          { dispatch, queryFulfilled, ...api },
        ) => {
          const state = api.getState();
          const regionSelector = edgeApi.endpoints.getRegions.select(undefined);
          const region = regionSelector(state).data?.find(
            (region) => region.id === regionId,
          );
          const zoneSelector = edgeApi.endpoints.getZones.select(undefined);
          const zone = zoneSelector(state).data?.[zoneId];
          const { undo: undoRegion } = dispatch(
            edgeApi.util.updateQueryData('getRegions', undefined, (draft) => {
              const index = draft.findIndex((region) => region.id === regionId);
              if (zone && index !== -1) {
                draft[index].zones.push(zone);
              }
            }),
          );
          const { undo: undoZone } = dispatch(
            edgeApi.util.updateQueryData('getZones', undefined, (draft) => {
              if (region) {
                draft[zoneId]?.regions.push({
                  id: region.id,
                  name: region.name,
                });
              }
            }),
          );
          try {
            await queryFulfilled;
          } catch (e) {
            console.error(e);
            undoRegion();
            undoZone();
          }
        },
      },
    ),

    removeZoneFromRegion: build.mutation<
      Region,
      { regionId: number; zone: number }
    >({
      query: ({ regionId, zone }) => ({
        url: `/api/regions/${regionId}/remove_zone/`,
        method: 'POST',
        body: { zone },
      }),
      onQueryStarted: async (
        { regionId, zone: zoneId },
        { dispatch, queryFulfilled },
      ) => {
        const { undo: undoRegion } = dispatch(
          edgeApi.util.updateQueryData('getRegions', undefined, (draft) => {
            const index = draft.findIndex((region) => region.id === regionId);
            if (index !== -1) {
              draft[index].zones = draft[index].zones.filter(
                (zone) => zone.id !== zoneId,
              );
            }
          }),
        );
        const { undo: undoZone } = dispatch(
          edgeApi.util.updateQueryData('getZones', undefined, (draft) => {
            if (draft[zoneId]) {
              draft[zoneId].regions = draft[zoneId].regions.filter(
                (region) => region.id !== regionId,
              );
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch (e) {
          console.error(e);
          undoRegion();
          undoZone();
        }
      },
    }),

    splitTSPInRegion: build.mutation<Region, { regionId: number; tsp: number }>(
      {
        query: ({ regionId, tsp }) => ({
          url: `/api/regions/${regionId}/split_tsp/`,
          method: 'POST',
          body: { tsp },
        }),
        onQueryStarted: async (arg, { dispatch, queryFulfilled, ...api }) => {
          const state = api.getState();
          const regionId = arg.regionId;
          const tspId = arg.tsp;
          const regionSelector = edgeApi.endpoints.getRegions.select(undefined);
          const region = regionSelector(state).data?.find(
            (r) => r.id === regionId,
          );
          const zonesSelector = edgeApi.endpoints.getZones.select(undefined);
          const zones = Object.values(zonesSelector(state).data ?? {})
            .filter((zone) => zone.tsp && zone.tsp.id === tspId)
            .map((zone) => zone as RegionZone);

          const { undo: undoRegion } = dispatch(
            edgeApi.util.updateQueryData('getRegions', undefined, (draft) => {
              const index = draft.findIndex((region) => region.id === regionId);
              if (index !== -1) {
                draft[index].tsps = draft[index].tsps.filter(
                  (t) => t.id !== tspId,
                );
                draft[index].zones = draft[index].zones.filter((zone) =>
                  zones.every((newZone) => newZone.id !== zone.id),
                );
                draft[index].zones.push(...zones);
              }
            }),
          );
          const { undo: undoZone } = dispatch(
            edgeApi.util.updateQueryData('getZones', undefined, (draft) => {
              zones.forEach((zone) => {
                if (
                  draft[zone.id] &&
                  draft[zone.id].regions.every((r) => r.id !== regionId)
                ) {
                  draft[zone.id].regions.push({
                    id: regionId,
                    name: region?.name ?? '<unknown>',
                  });
                }
              });
            }),
          );
          const { undo: undoTSP } = dispatch(
            edgeApi.util.updateQueryData('getTSPs', undefined, (draft) => {
              if (draft[tspId]) {
                draft[tspId].regions = draft[tspId].regions.filter(
                  (region) => region.id !== regionId,
                );
              }
            }),
          );
          try {
            await queryFulfilled;
          } catch (e) {
            console.error(e);
            undoRegion();
            undoZone();
            undoTSP();
          }
        },
      },
    ),

    createUpdateContract: build.mutation({
      async queryFn(
        contract: Partial<Contract>,
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        let contractRes;
        if (contract.id === 'new' || !contract.id) {
          contractRes = (await fetchWithBQ({
            url: '/api/contracts/',
            method: 'POST',
            body: contract,
          })) as QueryReturnValue<Contract>;
        } else {
          contractRes = (await fetchWithBQ({
            url: `/api/contracts/${contract.id}/`,
            method: 'PATCH',
            body: contract,
          })) as QueryReturnValue<Contract>;
        }

        return contractRes;
      },
      invalidatesTags: [{ type: 'Contract', id: 'LIST' }],
    }),

    getOrganizations: build.query({
      query: (args: { mineOnly?: boolean } = {}) => {
        const { mineOnly = false } = args;
        return `/api/organizations/${mineOnly ? '?mine_only=true' : ''}`;
      },
      transformResponse: (response: { results: Organization[] }) => {
        return response.results;
      },
      providesTags: [{ type: 'Organization', id: 'LIST' }],
    }),

    createUpdateScenario: build.mutation({
      async queryFn(
        scenario: Partial<Scenario>,
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        let scenarioRes;
        const apiPayload = toApiFormat(scenario);
        if (scenario.id === 'new' || !scenario.id) {
          scenarioRes = (await fetchWithBQ({
            url: '/api/scenarios/',
            method: 'POST',
            body: apiPayload,
          })) as QueryReturnValue<Scenario>;
        } else {
          scenarioRes = (await fetchWithBQ({
            url: `/api/scenarios/${scenario.id}/`,
            method: 'PATCH',
            body: apiPayload,
          })) as QueryReturnValue<Scenario>;
        }

        return scenarioRes.data
          ? { data: fromApiFormat(scenarioRes.data) }
          : { error: scenarioRes.error };
      },
      invalidatesTags: [{ type: 'Scenario', id: 'LIST' }],
    }),

    getNotices: build.query({
      query: (date) => `/api/notices/?date=${date}`,
      transformResponse: (response: { results: Notice[] }) => response.results,
      providesTags: [{ type: 'Notice', id: 'LIST' }],
    }),

    getPrices: build.query({
      query: ({
        date = null,
        priceSource = 'POSTMORTEM_DAILY',
        strip = 'Next Day Gas',
        bidAsk = false,
        endpoint = '',
      }: {
        date?: string | null;
        priceSource?: PriceSource;
        strip?: string;
        bidAsk?: boolean;
        endpoint?: string;
      }) => {
        const queryParams = {
          gas_date: date, // TODO: switched to gas_date from date here for consistency but didn't want to change it in the rest of the frontend in case I missed something
          price_source: priceSource || 'POSTMORTEM_DAILY',
          strip: strip || 'Next Day Gas',
          bid_ask: bidAsk,
          endpoint: endpoint,
        };
        const queryString = buildQueryString(queryParams);
        return `/api/markets/generate/${endpoint}?${queryString}`;
      },
      transformResponse: (response: Market[], _meta, arg) =>
        response.map((result) => ({
          ...result,
          price_source: arg.priceSource,
        })),
    }),

    getCapacityComponents: build.query({
      query: ({
        capacityTypes = [],
        tsps = [],
        hasPoints,
        hasNoticeComponent = null,
        hasFlowActivity = null,
        hasNoticeActivity = null,
        pointId = null,
        textSearch = null,
        directions = [],
        includeLinkHealth = null,
        ...filters
      }: Partial<AdvancedFiltersState['capacityComponents']> & {
        limit?: number;
        offset?: number;
        pointId?: number | null;
        includeLinkHealth?: boolean | null;
      }) => {
        const queryParams = {
          capacity_type__in: capacityTypes?.join?.(','),
          tsp_id__in: tsps?.join?.(','),
          has_points: hasPoints,
          has_notice_component: hasNoticeComponent,
          has_recent_flows: hasFlowActivity,
          has_recent_notices: hasNoticeActivity,
          points__id: pointId,
          direction__in: directions?.join?.(','),
          search: textSearch,
          include_link_health: includeLinkHealth,
          ...filters,
        };
        const queryString = buildQueryString(queryParams);
        return `/api/capacity_components/?${queryString}`;
      },
      transformResponse: (response: PaginatedResponseDict<CapacityComponent>) =>
        response.results,
      providesTags: (result) => {
        return result
          ? [
              ...Object.keys(result).map(
                (key): { type: TagType; id: string } => ({
                  type: 'CapacityComponent',
                  id: key,
                }),
              ),
              { type: 'CapacityComponent', id: 'LIST' },
            ]
          : [{ type: 'CapacityComponent', id: 'LIST' }];
      },
    }),

    getCapacityComponentsFlows: build.query({
      query: ({
        gasDate = 0,
        nomCycle = 0,
        capacityType = null,
        pointId = null,
        filters = null,
        limit = null,
        offset = null,
        favorites = null,
        searchText = null,
        defaultFilter = false,
        tsps = [],
      }: {
        gasDate: string | number;
        nomCycle: string | number;
        capacityType: string | null;
        pointId: number | null;
        filters: string | null;
        limit: number | null;
        offset: number | null;
        favorites: boolean | null;
        searchText: string | null;
        defaultFilter: boolean;
        tsps?: number[] | string[];
      }) => {
        const queryParams = {
          gas_date: gasDate || null,
          nomination_cycle: nomCycle || null,
          points__id: pointId || null,
          limit,
          offset,
          favorites,
          capacity_type: capacityType || null,
          search: searchText || null,
          default_filter: defaultFilter ? 'True' : 'False',
          tsp_id__in: tsps.join?.(','),
        };
        const queryString = buildQueryString(queryParams);
        const filtersArg = filters || '';
        return `/api/capacity_components/flows/?${queryString}&${filtersArg}`;
      },
      transformResponse: (response: Record<string, Flow>) => response,
      providesTags: ['CapacityComponent'],
    }),

    getCapacityComponentFlowHistory: build.query({
      query: ({ componentId = 0, limit = null, offset = null }) => {
        const queryParams = {
          limit,
          offset,
        };
        const queryString = buildQueryString(queryParams);
        return `/api/capacity_components/${componentId}/history/?${queryString}`;
      },
      providesTags: ['CapacityComponent'],
    }),

    getCapacityComponent: build.query<
      CapacityComponent,
      { componentId: number | string }
    >({
      query: ({ componentId }) => `/api/capacity_components/${componentId}/`,
      providesTags: (_result, _error, arg) => [
        { type: 'CapacityComponent', id: String(arg.componentId) },
      ],
    }),

    getCapacityComponentCriterionLinks: build.query<
      CapacityComponentCriterionLink[],
      { componentId: number | string }
    >({
      query: ({ componentId }) =>
        `/api/capacity_components/${componentId}/criterion_links/`,
      providesTags: ['CapacityComponent'],
    }),

    getCapacityComponentCriterionLinkCandidates: build.query<
      CapacityComponentCriterionLink[],
      { componentId: number | string; search: string }
    >({
      query: ({ componentId, search }) =>
        `/api/capacity_components/${componentId}/criterion_links/?${buildQueryString(
          { search },
        )}`,
      providesTags: ['CapacityComponent'],
    }),

    updateCapacityComponentCriterionLinks: build.mutation<
      CapacityComponentCriterionLink[],
      {
        componentId: number | string;
        links: Array<{
          id: number;
          creates_flows: boolean;
          override_direction_sign?: number | null;
        }>;
      }
    >({
      query: ({ componentId, links }) => ({
        url: `/api/capacity_components/${componentId}/criterion_links/`,
        method: 'PATCH',
        body: { links },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'CapacityComponent', id: String(arg.componentId) },
        { type: 'CapacityComponent', id: 'LIST' },
      ],
    }),

    attachCapacityComponentCriterionLink: build.mutation<
      CapacityComponentCriterionLink,
      {
        componentId: number | string;
        metadataId: string;
        createsFlows?: boolean;
        forceReassign?: boolean;
        overrideDirectionSign?: number | null;
      }
    >({
      query: ({
        componentId,
        metadataId,
        createsFlows = true,
        forceReassign = false,
        overrideDirectionSign,
      }) => ({
        url: `/api/capacity_components/${componentId}/criterion_links/`,
        method: 'POST',
        body: {
          metadata_id: metadataId,
          creates_flows: createsFlows,
          force_reassign: forceReassign,
          override_direction_sign: overrideDirectionSign,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'CapacityComponent', id: String(arg.componentId) },
        { type: 'CapacityComponent', id: 'LIST' },
      ],
    }),

    detachCapacityComponentCriterionLink: build.mutation<
      void,
      {
        componentId: number | string;
        id: number;
      }
    >({
      query: ({ componentId, id }) => ({
        url: `/api/capacity_components/${componentId}/criterion_links/`,
        method: 'DELETE',
        body: { id },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'CapacityComponent', id: String(arg.componentId) },
        { type: 'CapacityComponent', id: 'LIST' },
      ],
    }),

    // POST-based flow history for FlowSliceKey
    getFlowSliceHistory: build.mutation<
      any,
      {
        key: FlowSliceKey;
        limit?: number;
        offset?: string;
      }
    >({
      query: ({ key, limit = 30, offset = null }) => ({
        url: '/api/capacity_components/flow_slice_history/',
        method: 'POST',
        body: {
          key,
          limit,
          offset,
        },
      }),
    }),

    getCapacityComponentBulkHistory: build.query<
      // Flow[],
      PathFlows,
      {
        componentIds: number[] | null;
        has_recent_flows?: boolean;
        limit?: number | null;
        offset?: string | null;
      }
    >({
      query: ({
        componentIds = [],
        has_recent_flows = true,
        limit = null,
        offset = null,
      }) => {
        const queryParams = {
          id: componentIds,
          has_recent_flows,
          limit,
          offset,
        };
        const queryString = buildQueryString(queryParams);
        return `/api/capacity_components/bulk_history/?${queryString}`;
      },
    }),

    // Get grouped rows for a path (parent summary + nested evidence rows)
    getPathFlows: build.mutation<PathFlows, GetPathFlowsRequest>({
      query: ({
        path_elements = [],
        component_elements = [],
        nomination_cycle = null,
        limit = 30,
        offset = new Date().toISOString().slice(0, 10),
        view = 'all',
        filter_by_direction = true,
        include_empty = false,
      }) => ({
        url: '/api/capacity_components/path_flows/',
        method: 'POST',
        body: {
          path_elements,
          component_elements,
          options: {
            nomination_cycle,
            limit,
            offset,
            view,
            filter_by_direction,
            include_empty,
          },
        },
      }),
    }),

    createUpdateCapacityComponent: build.mutation({
      async queryFn(
        capacityComponent: Partial<CapacityComponent>,
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        let capacityComponentRes;
        if (capacityComponent.id === 'new' || !capacityComponent.id) {
          capacityComponentRes = (await fetchWithBQ({
            url: '/api/capacity_components/',
            method: 'POST',
            body: capacityComponent,
          })) as QueryReturnValue<CapacityComponent>;
        } else {
          capacityComponentRes = (await fetchWithBQ({
            url: `/api/capacity_components/${capacityComponent.id}/`,
            method: 'PATCH',
            body: capacityComponent,
          })) as QueryReturnValue<CapacityComponent>;
        }

        return capacityComponentRes.data
          ? { data: capacityComponentRes.data }
          : { error: capacityComponentRes.error };
      },
      invalidatesTags: ['CapacityComponent'],
    }),

    attachCapacityComponentPoint: build.mutation({
      query: ({
        component,
        point,
      }: {
        component: CapacityComponent;
        point: Point;
      }) => ({
        url: `/api/capacity_components/${component.id}/attach_point/`,
        method: 'PUT',
        body: { point_id: point.id },
      }),
      async onQueryStarted({ component, point }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            edgeApi.util.invalidateTags([
              { type: 'CapacityComponent', id: component.id.toString() },
              { type: 'CapacityComponent', id: 'LIST' },
            ]),
          );
          dispatch(
            edgeApi.util.updateQueryData('getMapPoints', undefined, (draft) => {
              draft[point.id].capacity_components.push({
                id: Number(component.id),
                description: component.label ?? component.description,
              });
              return draft;
            }),
          );
          if (component.capacity_type === 'SEGMENT') {
            dispatch(
              edgeApi.util.updateQueryData(
                'getMapEdges',
                undefined,
                (draft) => {
                  if (
                    ['FORWARDHAUL', 'BIDIRECTIONAL', 'CALCULATED'].includes(
                      component.flow_direction,
                    )
                  ) {
                    Object.values(draft).forEach((edge) => {
                      if (edge.end_points.includes(Number(point.id))) {
                        edge.forwardhaul_capacity_components.push([
                          Number(component.id),
                          component.label ?? component.description,
                        ]);
                      }
                    });
                  }
                  if (
                    ['BACKHAUL', 'BIDIRECTIONAL', 'CALCULATED'].includes(
                      component.flow_direction,
                    )
                  ) {
                    Object.values(draft).forEach((edge) => {
                      if (edge.start_points.includes(Number(point.id))) {
                        edge.backhaul_capacity_components.push([
                          Number(component.id),
                          component.label ?? component.description,
                        ]);
                      }
                    });
                  }
                  return draft;
                },
              ),
            );
          }
        } catch (error) {
          console.error('Error attaching point to capacity component:', error);
        }
      },
    }),

    detachCapacityComponentPoint: build.mutation({
      query: ({
        component,
        point,
      }: {
        component: CapacityComponent;
        point: Point;
      }) => ({
        url: `/api/capacity_components/${component.id}/detach_point/`,
        method: 'PATCH',
        body: { point_id: point.id },
      }),
      async onQueryStarted({ component, point }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            edgeApi.util.invalidateTags([
              { type: 'CapacityComponent', id: component.id.toString() },
              { type: 'CapacityComponent', id: 'LIST' },
            ]),
          );
          dispatch(
            edgeApi.util.updateQueryData('getMapPoints', undefined, (draft) => {
              draft[point.id].capacity_components = draft[
                point.id
              ].capacity_components.filter(
                (pointComponent) => component.id !== pointComponent.id,
              );
              return draft;
            }),
          );
          if (component.capacity_type === 'SEGMENT') {
            dispatch(
              edgeApi.util.updateQueryData(
                'getMapEdges',
                undefined,
                (draft) => {
                  if (
                    ['FORWARDHAUL', 'BIDIRECTIONAL', 'CALCULATED'].includes(
                      component.flow_direction,
                    )
                  ) {
                    Object.values(draft).forEach((edge) => {
                      if (edge.end_points.includes(Number(point.id))) {
                        edge.forwardhaul_capacity_components =
                          edge.forwardhaul_capacity_components.filter(
                            ([componentId]) =>
                              componentId !== Number(component.id),
                          );
                      }
                    });
                  }
                  if (
                    ['BACKHAUL', 'BIDIRECTIONAL', 'CALCULATED'].includes(
                      component.flow_direction,
                    )
                  ) {
                    Object.values(draft).forEach((edge) => {
                      if (edge.start_points.includes(Number(point.id))) {
                        edge.backhaul_capacity_components =
                          edge.backhaul_capacity_components.filter(
                            ([componentId]) =>
                              componentId !== Number(component.id),
                          );
                      }
                    });
                  }
                  return draft;
                },
              ),
            );
          }
        } catch (error) {
          console.error(
            'Error detaching point from capacity component:',
            error,
          );
        }
      },
    }),

    getPriceComponents: build.query({
      query: ({
        limit,
        offset,
        hasPoints,
        mapped,
        tsps,
        priceTypes,
        textSearch,
      }) => {
        const queryStr = buildQueryString({
          search: textSearch || null,
          limit: limit || null,
          offset: offset || null,
          has_points: hasPoints ?? null,
          is_mapped: mapped ?? null,
          buy_sell__in: priceTypes?.length ? priceTypes.join(',') : null,
          points__tsp__id__in: tsps?.length ? tsps.join(',') : null,
        });
        return `/api/price_components/?${queryStr}`;
      },
      transformResponse: (response: PaginatedResponseDict<PriceComponent>) =>
        response.results,
      providesTags: ['PriceComponent'],
    }),

    getPriceComponentPrices: build.query({
      query: ({
        gasDate = 0,
        pointId,
        searchQuery,
        useInScenarios,
        priceSource,
      }: {
        gasDate: string | number;
        pointId?: number;
        searchQuery?: string;
        useInScenarios?: boolean;
        priceSource: PriceSource;
      }) => {
        const queryParams = {
          use_in_scenarios: useInScenarios || null,
          gas_date: gasDate || null,
          points__id: pointId || null,
          search: searchQuery || null,
          price_source: priceSource,
        };
        const queryString = buildQueryString(queryParams);
        if (!searchQuery) {
          return `/api/price_components/prices/?${queryString}`;
        } else {
          return `/api/price_components/?${queryString}`;
        }
      },
      // TODO: This type is incorrect, /api/price_components/prices returns a subtype of PriceComponent
      transformResponse: (response: PaginatedResponseDict<PriceComponent>) =>
        response.results,
      providesTags: ['PriceComponentFlow'],
    }),

    getPriceComponent: build.query({
      // TODO: this is supposed to get price_components but also retrieve their historic records - changed the backend endpoint to /price_components/:id/prices - set up a new endpoint for getPriceComponentHistory
      query: ({ componentId = 0 }) => `/api/price_components/${componentId}/`,
      providesTags: ['PriceComponent'],
      // transformResponse: (response) => response.results,
    }),

    getPriceComponentHistory: build.query({
      query: ({ componentId = 0, priceSource, cursor, pageSize }) => {
        const queryParams = {
          price_source: priceSource,
          cursor,
          page_size: pageSize,
        };
        const queryString = buildQueryString(queryParams);
        return `/api/price_components/${componentId}/history/?${queryString}`;
      },
    }),

    createUpdatePriceComponent: build.mutation({
      async queryFn(
        priceComponent: Partial<PriceComponent>,
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        let priceComponentRes;
        if (priceComponent.id === 'new' || !priceComponent.id) {
          priceComponentRes = (await fetchWithBQ({
            url: '/api/price_components/',
            method: 'POST',
            body: priceComponent,
          })) as QueryReturnValue<PriceComponent>;
        } else {
          priceComponentRes = (await fetchWithBQ({
            url: `/api/price_components/${priceComponent.id}/`,
            method: 'PATCH',
            body: priceComponent,
          })) as QueryReturnValue<PriceComponent>;
        }

        return priceComponentRes.data
          ? { data: priceComponentRes.data }
          : { error: priceComponentRes.error };
      },
      invalidatesTags: ['PriceComponent'],
    }),

    attachPriceComponentPoint: build.mutation({
      query: ({
        component,
        point,
      }: {
        component: PriceComponent;
        point: Point;
      }) => ({
        url: `/api/price_components/${component.id}/attach_point/`,
        method: 'PUT',
        body: { point_id: point.id },
      }),
      async onQueryStarted({ component, point }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            edgeApi.util.invalidateTags([
              { type: 'PriceComponent', id: component.id.toString() },
              { type: 'PriceComponent', id: 'LIST' },
            ]),
          );
          dispatch(
            edgeApi.util.updateQueryData(
              'getPriceComponents',
              undefined,
              (draft) => {
                draft[component.id] = component;
                return draft;
              },
            ),
          );
          dispatch(
            edgeApi.util.updateQueryData('getMapPoints', undefined, (draft) => {
              draft[point.id].price_components.push({
                id: Number(component.id),
                description: component.description,
                buy_sell: labelToBuyOrSell(component.buy_sell),
              });
              return draft;
            }),
          );
        } catch (error) {
          console.error('Error attaching point to price component:', error);
        }
      },
    }),

    detachPriceComponentPoint: build.mutation({
      query: ({
        component,
        point,
      }: {
        component: PriceComponent;
        point: Point;
      }) => ({
        url: `/api/price_components/${component.id}/detach_point/`,
        method: 'PATCH',
        body: { point_id: point.id },
      }),
      async onQueryStarted({ component, point }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            edgeApi.util.invalidateTags([
              { type: 'PriceComponent', id: component.id.toString() },
              { type: 'PriceComponent', id: 'LIST' },
            ]),
          );
          dispatch(
            edgeApi.util.updateQueryData(
              'getPriceComponents',
              undefined,
              (draft) => {
                draft[component.id] = component;
                return draft;
              },
            ),
          );
          dispatch(
            edgeApi.util.updateQueryData('getMapPoints', undefined, (draft) => {
              draft[point.id].price_components = draft[
                point.id
              ].price_components.filter(
                (pc: { id: number }) => pc.id !== component.id,
              );
              return draft;
            }),
          );
        } catch (error) {
          console.error(
            'Error detaching point from capacity component:',
            error,
          );
        }
      },
    }),

    getSegmentConstraints: build.query<SegmentConstraint[], { date: string }>({
      query: ({ date }) => {
        return `/api/segment_constraints/from_best_available/?gas_date=${date}`;
      },
    }),

    getPointConstraints: build.query<PointConstraint[], { date: string }>({
      query: ({ date }) => {
        return `/api/point_constraints/from_best_available/?gas_date=${date}`;
      },
    }),

    optimizeScenario: build.mutation({
      async queryFn(scenario: Scenario, _queryApi, _extraOptions, fetchWithBQ) {
        const operationRes = (await fetchWithBQ({
          url: '/api/optimize/',
          method: 'POST',
          body: { scenario },
        })) as QueryReturnValue<Scenario>;
        return operationRes.data
          ? { data: operationRes.data }
          : { error: operationRes.error };
      },
      invalidatesTags: [{ type: 'Scenario' }],
    }),

    getPlattsPipelineFlows: build.query({
      query: ({ urlFilters }) => `/api/platts_pipeline_flows/?${urlFilters}`,
    }),

    getPoints: build.query({
      query: ({
        searchQuery,
        filters,
      }: {
        searchQuery: string;
        filters: string;
      }) => `/api/points/?search=${searchQuery}&${filters}`,
      transformResponse: (response: Record<string, Point>) => response.results,
    }),

    favoriteItem: build.mutation({
      query: ({ itemType, itemId }) => ({
        method: 'POST',
        url: '/api/favorites/',
        body: {
          item_type: itemType,
          item_id: itemId,
        },
      }),
    }),

    getFavorites: build.query({
      query: ({ itemId = null, itemType = null }) => {
        const queryParams = {
          item_id: itemId,
          item_type: itemType,
        };
        const queryString = buildQueryString(queryParams);
        return `/api/favorites/?${queryString}`;
      },
    }),

    unfavoriteItem: build.mutation({
      query: ({ itemId, itemType }) => ({
        method: 'DELETE',
        url: '/api/favorites/unfavorite/',
        body: {
          item_id: itemId,
          item_type: itemType,
        },
      }),
    }),

    getExtractedNotices: build.query<
      ExtractedNotice[],
      {
        gasDate?: string | null;
        tsps?: number[];
        limit?: number;
        offset?: number;
        search?: string;
      }
    >({
      query: ({
        gasDate = null,
        tsps = [],
        limit = 10,
        offset = 0,
        search,
      }) => {
        const queryParams = buildQueryString({
          gas_date: gasDate,
          tsp_id__in: tsps.join(','),
          limit,
          offset,
          search,
        });
        return `/api/extracted_notices/on_date/?${queryParams}`;
      },
    }),

    getExtractedNoticesStatus: build.query<
      NoticeStatus[],
      { startDate?: string | Date | null } | undefined
    >({
      query: ({ startDate } = {}) => {
        const queryParams = buildQueryString({
          start_date: startDate ? toIsoDate(startDate) : null,
        });

        return `/api/extracted_notices/status/?${queryParams}`;
      },
    }),

    getNoticeComponents: build.query<
      Record<string, NoticeComponent>,
      Partial<AdvancedFiltersState['noticeComponents']> & {
        gasDate?: string;
        offset?: number;
        limit?: number;
      }
    >({
      query: ({
        gasDate = null,
        textSearch = null,
        tsps = null,
        directions = [],
        capacityTypes = [],
        hasPoints = null,
        hasNoticeActivity = null,
        hasCapacityComponent = null,
        mapped = null,
        ...filters
      }) => {
        const queryParams = buildQueryString({
          gas_date: gasDate,
          search: textSearch,
          tsp_id__in: tsps?.join?.(','),
          capacity_component__flow_direction__in: directions?.join?.(','),
          capacity_component__capacity_type__in: capacityTypes?.join?.(','),
          has_points: hasPoints,
          has_recent_notices: hasNoticeActivity,
          has_capacity_component: hasCapacityComponent,
          is_mapped: mapped,
          ...filters,
        });
        return `/api/notice_components/?${queryParams}`;
      },
      transformResponse: (response: {
        results: Record<string, NoticeComponent>;
      }) => response.results,
    }),

    getNoticeComponentRecords: build.query<
      NoticeComponentRecords[],
      {
        gasDate?: string | null;
        search?: string;
        tsps?: number[];
        limit?: number;
        offset?: string;
      }
    >({
      query: ({
        gasDate = null,
        search = null,
        tsps = [],
        limit = null,
        offset = null,
      }) => {
        const queryParams = buildQueryString({
          gas_date: gasDate,
          search,
          tsp__in: tsps.join?.(','),
          limit,
          offset,
        });
        return `/api/notice_components/notices/?${queryParams}`;
      },
    }),

    getNoticeRecordHistory: build.query<
      NoticeRecord[],
      {
        componentId: number | string;
        limit?: number | null;
        offset?: number | null;
      }
    >({
      query: ({ componentId, limit = null, offset = null }) => {
        const queryParams = {
          notice_component: componentId,
          limit: limit,
          offset: offset,
        };
        const queryString = buildQueryString(queryParams);
        return `/api/notice_records/?${queryString}`;
      },
      transformResponse: (response: { results: NoticeRecord[] }) =>
        response.results,
    }),

    getNoticeComponent: build.query({
      query: ({ componentId = 0, limit = null, offset = null }) => {
        const queryParams = {
          limit: limit,
          offset: offset,
        };
        const queryString = buildQueryString(queryParams);
        return `/api/notice_components/${componentId ?? ''}/?${queryString}`;
      },
    }),

    getMaintenanceNotices: build.query<
      MaintenanceNoticeRecord[],
      {
        startGasDate?: string | null;
        endGasDate?: string | null;
        sourceTypes?: NoticeSourceType[];
        tsps?: number[];
        search?: string;
        limit?: number | null;
        offset?: number | null;
      }
    >({
      query: ({
        startGasDate = null,
        endGasDate = null,
        tsps = [],
        search = null,
        sourceTypes = [],
        limit = null,
        offset = null,
      }) => {
        const queryParams = buildQueryString({
          start_gas_date: startGasDate,
          end_gas_date: endGasDate,
          tsp__in: tsps.join(','),
          source_type__in: sourceTypes.join(','),
          search,
          limit,
          offset,
        });
        return `/api/notice_records/maintenance/?${queryParams}`;
      },
    }),

    getNoticeRecord: build.query<NoticeRecord, { recordId: number | string }>({
      query: ({ recordId }) => `/api/notice_records/${recordId}/`,
      // Remove transformResponse since it only affects a local responseCopy and throws errors parsing a Python JSON string.
    }),

    updateNoticeComponentCapacity: build.mutation<
      NoticeComponent,
      { componentId: number | string; capacityId: number | string | null }
    >({
      async queryFn(
        { componentId, capacityId },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        return (await fetchWithBQ({
          url: `/api/notice_components/${componentId}/assign_capacity_component/`,
          method: 'POST',
          body: { capacity_component_id: capacityId },
        })) as QueryReturnValue<NoticeComponent, any>;
      },
    }),

    createTraderPath: build.mutation({
      query: ({ chain_id, name }) => ({
        url: '/api/trader_paths/',
        method: 'POST',
        body: { chain_id, name },
      }),
    }),

    updateChainName: build.mutation({
      async queryFn({ id, name }, _queryApi, _extraOptions, fetchWithBQ) {
        const res = await fetchWithBQ({
          url: `/api/chain_info/${id}/`,
          method: 'PUT',
          body: { name },
        });
        return res.data ? { data: res.data } : { error: res.error };
      },
    }),

    getChainAnnotations: build.query({
      query({ id }) {
        return `/api/annotations/?item_id=${id}&item_type=ChainInfo`;
      },
    }),

    saveChainAnnotation: build.mutation({
      async queryFn({ id, annotation }, _queryApi, _extraOptions, fetchWithBQ) {
        const res = await fetchWithBQ({
          url: `/api/annotations/`,
          method: 'POST',
          body: { content: annotation, item_id: id, item_type: 'ChainInfo' },
        });
        return res.data ? { data: res.data } : { error: res.error };
      },
    }),

    getTagTypes: build.query({
      query: ({ name }) => `/api/tag_types/?search=${name}`,
    }),

    tagItem: build.mutation({
      async queryFn(
        { item_id, item_type, name },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const res = await fetchWithBQ({
          url: `/api/tags/`,
          method: 'POST',
          body: { item_id, item_type, name },
        });
        return res.data ? { data: res.data } : { error: res.error };
      },
    }),

    untagItem: build.mutation({
      async queryFn(
        { item_id, item_type, name },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const res = await fetchWithBQ({
          url: `/api/tags/untag/`,
          method: 'DELETE',
          body: { item_id, item_type, name },
        });
        return res.data ? { data: res.data } : { error: res.error };
      },
    }),

    getBaseload: build.query<
      Record<string, Trade>,
      { gasDate?: string | null }
    >({
      query: ({ gasDate = null }) => {
        const queryParams = buildQueryString({ gas_date: gasDate });
        return `/api/trades/baseload/?${queryParams}`;
      },
    }),

    getDayTrades: build.query<
      Record<string, Trade>,
      { gasDate?: string | null }
    >({
      query: ({ gasDate = null }) => {
        const queryParams = buildQueryString({ gas_date: gasDate });
        return `/api/trades/day_trades/?${queryParams}`;
      },
    }),

    getRiseTransportOnDate: build.query({
      query: ({ gasDate = null }) => {
        const queryParams = buildQueryString({ gas_date: gasDate });
        return `/api/rise_transport/transport_on_date/?${queryParams}`;
      },
    }),

    getRiseTransportPathOnDate: build.query<
      RiseChain[],
      { gasDate: string | null }
    >({
      query: ({ gasDate = null }) => {
        const queryParams = buildQueryString({ gas_date: gasDate });
        return `/api/rise_transport/transport_chains_on_date/?${queryParams}`;
      },
    }),

    lockScenario: build.mutation({
      query: ({ scenarioId, lockState }) => ({
        url: `/api/scenarios/${scenarioId}/lock/`,
        method: 'POST',
        body: { locked: lockState },
      }),
    }),

    listIOC: build.query({
      query: () => '/api/ioc/',
      providesTags: [{ type: 'IOC', id: 'LIST' }],
    }),

    listIOCContracts: build.query({
      query: (date = null) =>
        '/api/ioc_contracts/?' + buildQueryString({ date }),
      providesTags: [{ type: 'IOCContracts', id: 'LIST' }],
      transformResponse: (response: Record<string, IOCContract>) =>
        Object.values(response ?? {}),
    }),

    getRawIOCContract: build.query({
      query: (id) => `/api/ioc_contracts/${id}/raw`,
    }),

    submitIOC: build.mutation({
      query: ({ source, tsvFile, save, name }) => {
        const bodyFormData = new FormData();
        const queryString = buildQueryString({ source, save, name });
        bodyFormData.append('upload_data', tsvFile);
        return {
          url: `/api/ioc/?${queryString}`,
          method: 'POST',
          body: bodyFormData,
          formData: true,
        };
      },
      invalidatesTags: [{ type: 'IOC', id: 'LIST' }],
    }),

    // Path report endpoint (ensure braces/comma are correct)
    getPathReport: build.query<
      PathReportQueryResponse,
      PartialRecord<string, string | number | number[]> | void
    >({
      // this uses POST to send complex filters, but is still a query instead of a mutation to avoid flicker when querying new data
      query: (params) => {
        const body = {
          ...(params || {}),
          scenario_id: (params as any)?.scenario_id,
          gas_date: (params as any)?.gas_date,
        };
        return { url: '/api/path_report/query/', method: 'POST', body };
      },
      transformResponse: (
        response: PathReportQueryResponse,
      ): PathReportQueryResponse => {
        if (response.report_status !== 'READY') return response;
        return { ...response, paths: deduplicatePaths(response.paths) };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const sid = (queryArgs as any)?.scenario_id ?? 'default';
        const gd = (queryArgs as any)?.gas_date ?? '';
        const sb = (queryArgs as any)?.sort_by ?? '';
        return `${endpointName}|${sid}|${gd}|${sb}`;
      },
      providesTags: [{ type: 'PathReport', id: 'LIST' }],
    }),

    // Create a resolved path from a manual sequence (proxied via Django to FastAPI)
    createPathReportSequence: build.mutation<
      {
        status: 'ok';
        scenario_id: number;
        gas_date: string; // YYYY-MM-DD
        sequence: number[];
        paths: PathReport[];
        path_count: number;
      },
      {
        sequence: number[];
        gas_date?: string;
        best_contracts_only?: boolean;
        scenario_id?: string;
      }
    >({
      query: ({
        sequence,
        gas_date,
        best_contracts_only = true,
        scenario_id,
      }) => {
        return {
          url: '/api/path_report/resolve_path_from_sequence/',
          method: 'POST',
          body: { sequence, gas_date, best_contracts_only, scenario_id },
        };
      },
    }),

    getSegmentContractOptions: build.mutation<
      SegmentContractOptionsResponse,
      {
        segment_index: number;
        scenario_id?: string;
        gas_date?: string;
        path_spec: PathSpec;
        membership_id?: number;
        buy_asset_description?: string | null;
        sell_asset_description?: string | null;
      }
    >({
      query: (body) => ({
        url: '/api/path_report/contract_options_for_segment/',
        method: 'POST',
        body,
      }),
    }),

    getPriceDefinitionOptions: build.mutation<
      PriceDefinitionOptionsResponse,
      {
        scenario_id?: string;
        gas_date?: string;
      }
    >({
      query: (body) => ({
        url: '/api/path_report/price_definition_options/',
        method: 'POST',
        body,
      }),
    }),

    resolvePathSpec: build.mutation<
      ResolvePathSpecResponse,
      {
        scenario_id?: string;
        gas_date?: string;
        path_spec: PathSpec;
        buy_asset_description?: string | null;
        sell_asset_description?: string | null;
      }
    >({
      query: (body) => ({
        url: '/api/path_report/resolve_path_spec/',
        method: 'POST',
        body,
      }),
    }),

    getDashboard: build.query<Dashboard, number | string>({
      query: (dashboardId) => `/api/dashboards/${dashboardId}/`,
      providesTags: (_result, _error, dashboardId) => [
        { type: 'Dashboard', id: dashboardId },
      ],
    }),

    getDashboards: build.query<Dashboard[], void>({
      query: () => '/api/dashboards/',
      transformResponse: (response: { results: Dashboard[] } | Dashboard[]) => {
        if (Array.isArray(response)) {
          return response;
        }
        return response.results || [];
      },
      providesTags: () => [{ type: 'Dashboard', id: 'LIST' }],
    }),

    createDashboard: build.mutation<
      Dashboard,
      { name: string; description?: string | null }
    >({
      query: (body) => ({
        url: '/api/dashboards/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Dashboard', id: 'LIST' }],
    }),

    updateDashboard: build.mutation<
      Dashboard,
      {
        dashboardId: number | string;
        name?: string;
        description?: string | null;
        layout?: Record<string, any>;
      }
    >({
      query: ({ dashboardId, ...body }) => ({
        url: `/api/dashboards/${dashboardId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { dashboardId }) => [
        { type: 'Dashboard', id: 'LIST' },
        { type: 'Dashboard', id: dashboardId },
      ],
    }),

    createDashboardWidget: build.mutation<
      DashboardWidget,
      {
        dashboardId: number | string;
        widget_type?: 'FLOW_WATCHLIST';
        title?: string;
        order_index?: number;
        layout?: Record<string, any>;
        config?: Record<string, any>;
      }
    >({
      query: ({ dashboardId, ...body }) => ({
        url: `/api/dashboards/${dashboardId}/create_widget/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { dashboardId }) => [
        { type: 'Dashboard', id: dashboardId },
      ],
    }),

    updateDashboardWidget: build.mutation<
      DashboardWidget,
      {
        dashboardId: number | string;
        widgetId: number | string;
        title?: string;
        order_index?: number;
        layout?: Record<string, any>;
        config?: Record<string, any>;
      }
    >({
      query: ({ dashboardId, widgetId, ...body }) => ({
        url: `/api/dashboards/${dashboardId}/widgets/${widgetId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { dashboardId }) => [
        { type: 'Dashboard', id: dashboardId },
      ],
    }),

    deleteDashboardWidget: build.mutation<
      void,
      {
        dashboardId: number | string;
        widgetId: number | string;
      }
    >({
      query: ({ dashboardId, widgetId }) => ({
        url: `/api/dashboards/${dashboardId}/widgets/${widgetId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { dashboardId }) => [
        { type: 'Dashboard', id: dashboardId },
      ],
    }),

    reorderDashboardWidgets: build.mutation<
      Dashboard,
      {
        dashboardId: number | string;
        widget_ids: number[];
      }
    >({
      query: ({ dashboardId, widget_ids }) => ({
        url: `/api/dashboards/${dashboardId}/reorder_widgets/`,
        method: 'POST',
        body: { widget_ids },
      }),
      invalidatesTags: (_result, _error, { dashboardId }) => [
        { type: 'Dashboard', id: dashboardId },
      ],
    }),

    // ---- Watchlists ---------
    getWatchlists: build.query<
      any[], // Type is any to handle both basic {id, name} and full watchlist data with paths
      {
        view_all?: boolean;
        with_paths?: boolean;
        path_fingerprint?: string;
      } | void
    >({
      query: (args) => {
        const qs = buildQueryString(args || {});
        return `/api/watchlists/?${qs}`;
      },
      providesTags: (_result, _error, args) => {
        const hasWithPaths = args?.with_paths === true;
        const hasPathFingerprint = Boolean(args?.path_fingerprint);

        const tags: { type: 'Watchlist'; id: string }[] = [
          { type: 'Watchlist', id: 'LIST' },
        ];

        if (hasWithPaths) {
          tags.push({ type: 'Watchlist', id: 'LIST_WITH_PATHS' });
        } else if (hasPathFingerprint) {
          tags.push({ type: 'Watchlist', id: 'LIST_MEMBERSHIP' });
        } else {
          tags.push({ type: 'Watchlist', id: 'LIST_BASIC' });
        }

        return tags;
      },
    }),

    createWatchlist: build.mutation<
      { id: number; name: string },
      { name: string }
    >({
      query: ({ name }) => ({
        url: '/api/watchlists/',
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: [{ type: 'Watchlist', id: 'LIST' }],
    }),

    deleteWatchlist: build.mutation<any, { watchlistId: number }>({
      query: ({ watchlistId }) => ({
        url: `/api/watchlists/${watchlistId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Watchlist', id: 'LIST' }],
    }),

    //need to implement addPathToFavorites api call
    addPathToFavorites: build.mutation<any, string>({
      query: (path_fingerprint) => ({
        url: '/api/watchlists/favorites/',
        method: 'POST',
        body: { path_fingerprint: path_fingerprint },
      }),
      invalidatesTags: [{ type: 'Watchlist', id: 'LIST' }],
    }),

    addPathToWatchlist: build.mutation<
      any,
      {
        watchlistId: number;
        path_fingerprint: string;
        name?: string;
        path_spec: PathSpec;
        membership_id?: number;
        scenario_id?: string;
        gas_date?: string;
      }
    >({
      query: ({
        watchlistId,
        path_fingerprint,
        name,
        path_spec,
        membership_id,
      }) => ({
        url: '/api/path_report/watchlist_append_paths/',
        method: 'POST',
        body: {
          watchlist_id: watchlistId,
          paths_dict: { [path_fingerprint]: name || null },
          path_specs: { [path_fingerprint]: path_spec },
          membership_ids:
            membership_id == null ? {} : { [path_fingerprint]: membership_id },
        },
      }),
      invalidatesTags: [
        { type: 'Watchlist', id: 'LIST_WITH_PATHS' },
        { type: 'Watchlist', id: 'LIST_MEMBERSHIP' },
      ],
    }),

    removePathFromWatchlist: build.mutation<
      any,
      { watchlistId: number; path_fingerprint: string; membership_id?: number }
    >({
      query: ({ watchlistId, path_fingerprint, membership_id }) => ({
        url: '/api/path_report/watchlist_remove_paths/',
        method: 'POST',
        body: {
          watchlist_id: watchlistId,
          path_fingerprints: [path_fingerprint],
          membership_ids: membership_id == null ? [] : [membership_id],
        },
      }),
      invalidatesTags: [
        { type: 'Watchlist', id: 'LIST_WITH_PATHS' },
        { type: 'Watchlist', id: 'LIST_MEMBERSHIP' },
      ],
    }),

    // ---- K shortest paths (FastAPI path_service) ----
    kPaths: build.mutation<
      any,
      {
        scenario_id?: string;
        gas_date?: string;
        regions?: number[];
        // Either specific point ids or asset tokens (point ids)
        receipt_point_id?: number;
        delivery_point_id?: number;
        receipt_point_ids?: number[];
        delivery_point_ids?: number[];
        receipt_asset_tokens?: (number | string | Record<string, any>)[];
        delivery_asset_tokens?: (number | string | Record<string, any>)[];
        mode?:
          | 'specific'
          | 'any_to_delivery'
          | 'receipt_to_any'
          | 'sets'
          | 'any_to_any'
          | 'diverse';
        inject_ad_hoc_segments?: boolean;
        allow_non_asset_terminals?: boolean;
        max_ad_hoc_points?: number;
        max_injected_segments?: number;
        k?: number;
        require_volume?: boolean;
        min_volume?: number;
        resolve?: boolean;
        interconnect_penalty?: number;
        exclude_tsp_ids?: number[];
        avoid_constraints?: { constraint_key: string; max_rank: number }[];
        contract_usages?: {
          contract_id: number;
          receipt_point_id: number;
          delivery_point_id: number;
          delivery_volume: number | string;
        }[];
        contract_usage_mode?: 'enforce' | 'report';
      }
    >({
      query: (payload) => {
        return {
          url: '/api/path_report/k_paths/',
          method: 'POST',
          body: payload,
        };
      },
    }),

    syncPathReportAssets: build.mutation<
      any,
      {
        scenario_id?: string;
        gas_date?: string;
        assets: {
          point_id: number;
          buy_sell: 1 | 2 | 3;
          gas_date?: string;
          base_price?: number;
          max_volume?: number;
          adder?: number;
          description?: string;
          price_source?: string;
          pool?: string | null;
        }[];
        max_ad_hoc_points?: number;
        max_injected_segments?: number;
      }
    >({
      query: (payload) => {
        return {
          url: '/api/path_report/sync_assets/',
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: [{ type: 'PathReportAssets', id: 'LIST' }],
    }),

    // ---- Assets (proxied via Django to FastAPI) ----
    getPathReportAssets: build.query<
      any,
      { scenario_id?: string; gas_date?: string | null }
    >({
      query: ({ scenario_id, gas_date } = {}) => {
        const params = {
          scenario_id: scenario_id ?? 'default',
          gas_date: gas_date ?? null,
        };
        return { url: '/api/path_report/assets/', method: 'GET', params };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const sid = (queryArgs as any)?.scenario_id ?? 'default';
        const gd = (queryArgs as any)?.gas_date ?? '';
        return `${endpointName}|${sid}|${gd}`;
      },
      providesTags: [{ type: 'PathReportAssets', id: 'LIST' }],
    }),

    // ---- Path Service: list available states (proxied via Django) ----
    getPathReportStates: build.query<
      {
        count: number;
        states: {
          scenario_id: string;
          gas_date: string;
          finder_url: string;
          report_url: string;
          builder_url: string;
        }[];
      },
      void
    >({
      query: () => '/api/path_report/states/',
    }),

    getKatanaPathDump: build.query<
      PaginatedResponse<KatanaPathDumpRow | KatanaPathSummary>,
      KatanaPathDumpQueryParams
    >({
      query: ({
        start_date,
        end_date,
        tsp_ids,
        path_r_point_ids,
        path_d_point_ids,
        view,
        limit,
        offset,
      }) => ({
        url: '/api/katana_paths/dump/',
        params: {
          start_date,
          end_date,
          tsp_ids,
          path_r_point_ids,
          path_d_point_ids,
          view,
          limit,
          offset,
        },
      }),
    }),

    getKatanaContractUsages: build.query<
      KatanaContractUsageResponse,
      { gas_date: string }
    >({
      query: ({ gas_date }) => ({
        url: '/api/katana_nominations/contract_usages/',
        params: { gas_date },
      }),
    }),

    getKatanaPaths: build.query<
      PaginatedResponse<KatanaPath>,
      {
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: '/api/katana_paths/',
        params: params ?? undefined,
      }),
    }),

    getKatanaPathingSummary: build.query<
      KatanaPathingSummaryDay | KatanaPathingSummaryDay[],
      KatanaPathingSummaryQueryParams | void
    >({
      query: (params) => ({
        url: '/api/katana_paths/pathing_summary/',
        params: params ?? undefined,
      }),
    }),

    getKatanaPathDetails: build.query<
      KatanaPathDetailsResponse,
      {
        id: number;
      }
    >({
      query: ({ id }) => ({
        url: '/api/katana_paths/details/',
        params: {
          id,
        },
      }),
    }),

    getKatanaTranslationPreview: build.query<
      | PaginatedResponse<KatanaTranslationPreviewRow>
      | KatanaTranslationPreviewRow[],
      Omit<KatanaPathDumpQueryParams, 'view'>
    >({
      query: ({
        start_date,
        end_date,
        tsp_ids,
        path_r_point_ids,
        path_d_point_ids,
        limit,
        offset,
      }) => ({
        url: '/api/katana_paths/translation_preview/',
        params: {
          start_date,
          end_date,
          tsp_ids,
          path_r_point_ids,
          path_d_point_ids,
          limit,
          offset,
        },
      }),
    }),

    translateKatanaPathsToWatchlist: build.mutation<
      KatanaTranslateToWatchlistResponse,
      KatanaTranslateToWatchlistRequest
    >({
      query: ({
        watchlist_id,
        persist,
        start_date,
        end_date,
        tsp_ids,
        path_r_point_ids,
        path_d_point_ids,
        ids,
      }) => ({
        url: '/api/katana_paths/translate_to_watchlist/',
        method: 'POST',
        params: {
          watchlist_id,
          persist,
          start_date,
          end_date,
          tsp_ids,
          path_r_point_ids,
          path_d_point_ids,
        },
        body: {
          watchlist_id,
          persist,
          start_date,
          end_date,
          tsp_ids,
          path_r_point_ids,
          path_d_point_ids,
          ids,
        },
      }),
      invalidatesTags: [{ type: 'Watchlist', id: 'LIST' }],
    }),

    getKatanaSequenceTranslationPreview: build.mutation<
      | PaginatedResponse<KatanaTranslationPreviewRow>
      | KatanaTranslationPreviewRow[],
      KatanaSequenceTranslationPreviewRequest
    >({
      query: ({ sequences }) => ({
        url: '/api/katana_paths/sequence_translation_preview/',
        method: 'POST',
        body: {
          sequences,
        },
      }),
    }),

    getKatanaTSPs: build.query<
      PaginatedResponse<KatanaTSP>,
      {
        page?: number;
        search?: string;
        id?: number;
        katana_id?: number;
        company_id?: number;
        tsp_id?: number;
        id__in?: number[];
        katana_id__in?: number[];
        company_id__in?: number[];
        tsp_id__in?: number[];
      } | void
    >({
      query: (params) => {
        const queryParams = buildQueryString({
          ...params,
          id__in: params?.id__in?.join(','),
          katana_id__in: params?.katana_id__in?.join(','),
          company_id__in: params?.company_id__in?.join(','),
          tsp_id__in: params?.tsp_id__in?.join(','),
        });
        return `/api/katana_tsps/?${queryParams}`;
      },
    }),

    getKatanaPoints: build.query<
      PaginatedResponse<KatanaPoint>,
      {
        page?: number;
        search?: string;
        id?: number;
        katana_id?: number;
        katana_tsp_id?: number;
        katana_gas_zone_id?: number;
        point_id?: number;
        point_type?: string;
        id__in?: number[];
        katana_id__in?: number[];
        katana_tsp_id__in?: number[];
        katana_gas_zone_id__in?: number[];
        point_id__in?: number[];
        point_type__in?: string[];
      } | void
    >({
      query: (params) => {
        const queryParams = buildQueryString({
          ...params,
          id__in: params?.id__in?.join(','),
          katana_id__in: params?.katana_id__in?.join(','),
          katana_tsp_id__in: params?.katana_tsp_id__in?.join(','),
          katana_gas_zone_id__in: params?.katana_gas_zone_id__in?.join(','),
          point_id__in: params?.point_id__in?.join(','),
          point_type__in: params?.point_type__in?.join(','),
        });
        return `/api/katana_points/?${queryParams}`;
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMapPointsQuery,
  useGetMapEdgesQuery,
  useGetMapNodesQuery,
  useGetMapLayoutQuery,
  useGetPointsQuery,
  useLoginUserMutation,
  useUpdatePointMutation,
  useCreateUpdatePointMutation,
  useCreatePointMutation,
  useCreateNodeMutation,
  useUpdateNodeMutation,
  useCreateUpdateEdgeMutation,
  useDeleteEdgeMutation,
  useSplitEdgeMutation,
  useDeleteNodeMutation,
  useAddNodePointMutation,
  useRemoveNodePointMutation,
  useDeletePointMutation,
  useGetPathQuery,
  useLazyGetPathQuery,
  useGetOverlaysQuery,
  useCreateUpdateOverlayMutation,
  useDeleteOverlayMutation,
  useGetScenarioQuery,
  useLazyGetScenarioQuery,
  useGetScenariosQuery,
  useLazyGetScenariosQuery,
  useGetScenarioGroupsQuery,
  useGetScenarioGroupQuery,
  useGetPermissionsQuery,
  useUpdatePasswordMutation,
  useGetInterconnectsQuery,
  useCreateInterconnectMutation,
  useDisableInterconnectMutation,
  useEnableInterconnectMutation,
  useDeleteInterconnectMutation,
  useOptimizeScenarioMutation,
  useGetNoticesQuery,
  useGetContractsQuery,
  useGetContractQuery,
  useGetContractGroupsQuery,
  useCreateContractGroupMutation,
  useCreateUpdateContractMutation,
  useCreateUpdateScenarioMutation,
  useGetTSPsQuery,
  useGetTSPPrioritiesQuery,
  useGetZonesQuery,
  useLazyGetZonesQuery,
  useGetRegionsQuery,
  useAddTSPToRegionMutation,
  useRemoveTSPFromRegionMutation,
  useAddZoneToRegionMutation,
  useRemoveZoneFromRegionMutation,
  useSplitTSPInRegionMutation,
  useGetPricesQuery,
  useLazyGetPricesQuery,
  useLazyGetSegmentConstraintsQuery,
  useLazyGetPointConstraintsQuery,
  useGetCapacityComponentsQuery,
  useLazyGetCapacityComponentsQuery,
  useGetCapacityComponentsFlowsQuery,
  useGetCapacityComponentQuery,
  useGetCapacityComponentFlowHistoryQuery,
  useGetCapacityComponentCriterionLinksQuery,
  useGetCapacityComponentCriterionLinkCandidatesQuery,
  useUpdateCapacityComponentCriterionLinksMutation,
  useAttachCapacityComponentCriterionLinkMutation,
  useDetachCapacityComponentCriterionLinkMutation,
  useCreateUpdateCapacityComponentMutation,
  useAttachCapacityComponentPointMutation,
  useDetachCapacityComponentPointMutation,
  useGetPriceComponentsQuery,
  useLazyGetPriceComponentsQuery,
  useGetPriceComponentPricesQuery,
  useLazyGetPriceComponentPricesQuery,
  useGetPriceComponentQuery,
  useGetPriceComponentHistoryQuery,
  useAttachPriceComponentPointMutation,
  useDetachPriceComponentPointMutation,
  useCreateUpdatePriceComponentMutation,
  useLazyGetContractsQuery,
  useLazyGetContractQuery,
  useCreateRateScheduleMutation,
  useGetRatesQuery,
  useAddRateMutation,
  useUpdateRatesMutation,
  useRemoveRateMutation,
  useCreateUpdateScenarioGroupMutation,
  useCreateScenarioGroupScenarioMutation,
  useDeleteScenarioGroupScenarioMutation,
  useGetPlattsPipelineFlowsQuery,
  useFavoriteItemMutation,
  useGetFavoritesQuery,
  useUnfavoriteItemMutation,
  useLazyGetExtractedNoticesQuery,
  useGetExtractedNoticesQuery,
  useGetExtractedNoticesStatusQuery,
  useLazyGetNoticeComponentsQuery,
  useGetNoticeComponentsQuery,
  useLazyGetNoticeComponentRecordsQuery,
  useGetNoticeComponentRecordsQuery,
  useGetNoticeRecordHistoryQuery,
  useLazyGetNoticeRecordHistoryQuery,
  useGetMaintenanceNoticesQuery,
  useLazyGetMaintenanceNoticesQuery,
  useGetNoticeRecordQuery,
  useGetNoticeComponentQuery,
  useUpdateNoticeComponentCapacityMutation,
  useCreateTraderPathMutation,
  useUpdateChainNameMutation,
  useLazyGetChainAnnotationsQuery,
  useSaveChainAnnotationMutation,
  useGetTagTypesQuery,
  useTagItemMutation,
  useUntagItemMutation,
  useLazyGetBaseloadQuery,
  useLazyGetDayTradesQuery,
  useGetRiseTransportOnDateQuery,
  useGetRiseTransportPathOnDateQuery,
  useLazyGetRiseTransportPathOnDateQuery,
  useLockScenarioMutation,
  useStartScenarioAllPathsMutation,
  useLazyGetScenarioAllPathsQuery,
  usePickFromScenarioAllPathsMutation,
  useSubmitIOCMutation,
  useListIOCQuery,
  useListIOCContractsQuery,
  useSyncPathReportAssetsMutation,
  useLazyGetRawIOCContractQuery,
  useGetOrganizationsQuery,
  useGetPathReportQuery,
  useGetCapacityComponentBulkHistoryQuery,
  useGetFlowSliceHistoryMutation,
  useGetDashboardsQuery,
  useGetDashboardQuery,
  useCreateDashboardMutation,
  useUpdateDashboardMutation,
  useCreateDashboardWidgetMutation,
  useUpdateDashboardWidgetMutation,
  useDeleteDashboardWidgetMutation,
  useReorderDashboardWidgetsMutation,
  useGetWatchlistsQuery,
  useCreateWatchlistMutation,
  useDeleteWatchlistMutation,
  useAddPathToFavoritesMutation,
  useAddPathToWatchlistMutation,
  useRemovePathFromWatchlistMutation,
  useCreatePathReportSequenceMutation,
  useGetSegmentContractOptionsMutation,
  useGetPriceDefinitionOptionsMutation,
  useResolvePathSpecMutation,
  useKPathsMutation,
  useGetPathReportAssetsQuery,
  useGetPathReportStatesQuery,
  useGetPathFlowsMutation,
  useGetKatanaPathsQuery,
  useGetKatanaPathDumpQuery,
  useLazyGetKatanaPathDumpQuery,
  useLazyGetKatanaContractUsagesQuery,
  useGetKatanaPathingSummaryQuery,
  useGetKatanaPathDetailsQuery,
  useLazyGetKatanaPathDetailsQuery,
  useGetKatanaTranslationPreviewQuery,
  useLazyGetKatanaTranslationPreviewQuery,
  useTranslateKatanaPathsToWatchlistMutation,
  useGetKatanaSequenceTranslationPreviewMutation,
  useGetKatanaTSPsQuery,
  useGetKatanaPointsQuery,
} = edgeApi;
